import { z } from 'zod';

/**
 * Search parameters for the workflow detail (read-only graph) route.
 *
 * Search parameters are user-editable text, so an unrecognised value falls back
 * to the default rather than failing the route. Path parameters behave the other
 * way round: an unresolvable identifier is a not-found condition, because the
 * address names a resource that does not exist.
 *
 * `step` is the selected step's id: present and known → that node is selected
 * and its panel is open; present but unknown → no selection, because a search
 * parameter is view state and degrades rather than 404s. Cleared when the panel
 * closes.
 *
 * `edge` is a transition's canvas id — `<stepId>:<key>`, the same grammar the
 * builder's `sel=edge:…` uses — and it is an *alternative* to `step`, not a
 * refinement of it. A transition is a thing you select in its own right, with
 * fields of its own, exactly as in the builder; it was briefly a highlight
 * inside its step's panel here, which made the same object behave differently
 * depending on which mode you were reading it in.
 *
 * Two parameters rather than the builder's single `sel` only because `step`
 * predates this and appears in links people have already shared. Navigation
 * sets one or the other, and `graph/selection.ts` resolves the pair into the
 * union the panel is actually about. An `edge` naming no live transition
 * degrades to no selection, the same way an unknown `step` does.
 *
 * `phase` is the third alternative, and completes the set the builder has
 * always had. Clicking a band used to clear the selection — a band was
 * background here and a selectable object there — which is the last place the
 * two modes disagreed about what a workflow is made of.
 */
export const workflowDetailSearchSchema = z.object({
  step: z.string().optional().catch(undefined),
  edge: z.string().optional().catch(undefined),
  phase: z.string().optional().catch(undefined),
});

export type WorkflowDetailSearch = z.infer<typeof workflowDetailSearchSchema>;

/**
 * Search parameters for the builder route.
 *
 * `sel` is what the inspector is about, in one parameter rather than three:
 * `step:<id>`, `edge:<stepId>:<key>`, `phase:<id>`. Three parameters would make
 * illegal combinations representable — a step *and* an edge selected at once —
 * which is a state no code would then be allowed to assume away.
 *
 * `.catch(undefined)` for the same reason `step` has it: a selection is view
 * state, so an unparseable or stale one degrades to "nothing selected" instead
 * of failing the route. Parsing into the union happens in `builder/selection.ts`,
 * which also resolves it against the draft — a URL is a suggestion about view
 * state, never an assertion about what exists.
 */
export const builderSearchSchema = z.object({
  sel: z.string().optional().catch(undefined),
});

export type BuilderSearch = z.infer<typeof builderSearchSchema>;

/**
 * The states the workflow list can be narrowed to.
 *
 * Two members where the page's own control offers three: absence is "all". A
 * list is unfiltered until someone says otherwise, and the address says so by
 * carrying nothing rather than by carrying a word that means "no filter" —
 * the same shape `boardSearchSchema` uses for the board's state filter.
 */
export const WORKFLOW_STATUS_FILTERS = ['published', 'drafts'] as const;
export type WorkflowStatusFilter = (typeof WORKFLOW_STATUS_FILTERS)[number];

/**
 * Search parameters for the workflow list route.
 *
 * The filter lives here rather than in component state because it is exactly
 * the kind the constitution places in the address: shareable, and expected to
 * survive a reload. Overview's drafts count links straight into it.
 *
 * The list's text query deliberately stays component-local. Nothing links to a
 * search term, and every keystroke would otherwise become a history entry.
 */
export const workflowListSearchSchema = z.object({
  status: z.enum(WORKFLOW_STATUS_FILTERS).optional().catch(undefined),
});

export type WorkflowListSearch = z.infer<typeof workflowListSearchSchema>;
